import { UTCDate } from '@date-fns/utc';
import { endOfHour, startOfHour } from 'date-fns';
import { isNumber } from 'lodash';
import { HOUR_MS } from 'src/domain/commonConst';
import {
    EnergyUsageTimeseriesDto,
    getBaselineCaiso10d,
    getBaselineStatic,
    getPerformanceCaiso10d,
    getPerformanceStatic,
} from 'src/domain/energy/interface';
import { BaselineMode } from 'src/domain/energy/baselineMarket';
import { IDateRange } from 'src/pages/interface';

export type EnergyUsageTimeseriesByHourMapItem = {
    usageKw: number[];
    qualityOfReading: number[];
    flowDirection: number[];
    baselineCaiso10d: number[];
    performanceCaiso10dKw: number[];
};

export function groupEnergyUsageByHour(dateRange: IDateRange, energyData: EnergyUsageTimeseriesDto, baselineMode: BaselineMode = 'caiso') {
    if (!energyData?.values.length) return null;

    const getBaseline = baselineMode === 'static' ? getBaselineStatic : getBaselineCaiso10d;
    const getPerformance = baselineMode === 'static' ? getPerformanceStatic : getPerformanceCaiso10d;

    // init empty map for each hour in the date range
    const hourMap = new Map<number, EnergyUsageTimeseriesByHourMapItem>();
    const startTimestamp = startOfHour(new UTCDate(dateRange.start)).valueOf();
    const endTimestamp = endOfHour(new UTCDate(dateRange.end)).valueOf();

    const emptyRecord = {
        usageKw: [],
        qualityOfReading: [],
        flowDirection: [],
        baselineCaiso10d: [],
        performanceCaiso10dKw: [],
    };

    let cursor = startTimestamp;
    while (cursor < endTimestamp) {
        hourMap.set(cursor, emptyRecord);
        cursor += HOUR_MS;
    }

    // fill map with values
    energyData.values.forEach(val => {
        const [timestamp, usageKw, qualityOfReading, flowDirection] = val;

        const hourKey = startOfHour(new UTCDate(timestamp)).valueOf();

        if (!hourMap.has(hourKey)) {
            hourMap.set(hourKey, emptyRecord);
        }

        const item = structuredClone(hourMap.get(hourKey)!);

        item.usageKw.push(usageKw);
        item.qualityOfReading.push(qualityOfReading);
        item.flowDirection.push(flowDirection);

        if (baselineMode !== 'none') {
            const baseline = getBaseline(val);
            const performance = getPerformance(val);

            isNumber(baseline) && item.baselineCaiso10d.push(baseline);
            isNumber(performance) && item.performanceCaiso10dKw.push(performance);
        }

        hourMap.set(hourKey, item);
    });

    return hourMap;
}
